// src/pinball/PinballSoundManager.ts - Sound event interface for pinball

import { PinballSoundEvent } from './types';

export class PinballSoundManager {
  private muted: boolean = false;
  private audioContext: AudioContext | null = null;

  constructor() {
    // Defer AudioContext creation until user interaction
    this.muted = false;
  }

  private getAudioContext(): AudioContext | null {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch {
        return null;
      }
    }
    return this.audioContext;
  }

  play(event: PinballSoundEvent): void {
    if (this.muted) return;

    const ctx = this.getAudioContext();
    if (!ctx) return;

    // Generate simple synthesized sounds
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    const params = this.getSoundParams(event);
    oscillator.type = params.type;
    oscillator.frequency.setValueAtTime(params.frequency, ctx.currentTime);
    gainNode.gain.setValueAtTime(params.volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + params.duration);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + params.duration);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  async preloadSounds(): Promise<void> {
    // For synthesized sounds, nothing to preload
    // This method exists for future audio asset loading
  }

  private getSoundParams(event: PinballSoundEvent): { type: OscillatorType; frequency: number; duration: number; volume: number } {
    switch (event) {
      case 'flipper_activate':
        return { type: 'square', frequency: 200, duration: 0.05, volume: 0.3 };
      case 'flipper_release':
        return { type: 'square', frequency: 150, duration: 0.03, volume: 0.2 };
      case 'bumper_hit':
        return { type: 'sine', frequency: 440, duration: 0.1, volume: 0.4 };
      case 'pop_bumper_hit':
        return { type: 'sine', frequency: 660, duration: 0.12, volume: 0.5 };
      case 'target_hit':
        return { type: 'triangle', frequency: 550, duration: 0.08, volume: 0.3 };
      case 'drop_target_hit':
        return { type: 'triangle', frequency: 330, duration: 0.15, volume: 0.4 };
      case 'drop_target_bank_complete':
        return { type: 'sine', frequency: 880, duration: 0.3, volume: 0.5 };
      case 'ramp_complete':
        return { type: 'sine', frequency: 700, duration: 0.2, volume: 0.4 };
      case 'spinner_spin':
        return { type: 'sine', frequency: 1000, duration: 0.03, volume: 0.2 };
      case 'slingshot_hit':
        return { type: 'sawtooth', frequency: 300, duration: 0.06, volume: 0.3 };
      case 'drain':
        return { type: 'sawtooth', frequency: 100, duration: 0.5, volume: 0.4 };
      case 'plunger_release':
        return { type: 'sawtooth', frequency: 250, duration: 0.15, volume: 0.3 };
      case 'tilt_warning':
        return { type: 'square', frequency: 200, duration: 0.3, volume: 0.5 };
      case 'tilt':
        return { type: 'square', frequency: 100, duration: 0.8, volume: 0.6 };
      case 'multiball_start':
        return { type: 'sine', frequency: 500, duration: 0.5, volume: 0.5 };
      case 'jackpot':
        return { type: 'sine', frequency: 1000, duration: 1.0, volume: 0.6 };
      case 'high_score':
        return { type: 'sine', frequency: 800, duration: 0.8, volume: 0.5 };
      case 'ball_save_active':
        return { type: 'triangle', frequency: 600, duration: 0.2, volume: 0.3 };
      case 'combo':
        return { type: 'sine', frequency: 750, duration: 0.15, volume: 0.4 };
      default:
        return { type: 'sine', frequency: 440, duration: 0.1, volume: 0.3 };
    }
  }
}
